import React, { useState, useEffect } from 'react';
import { invoicesAPI, designerAPI } from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { formatCurrency, formatDate, generateWhatsAppLink, generatePaymentReminderMessage } from '../utils/helpers';
import { WhatsappLogo, Warning } from '@phosphor-icons/react';

export const PaymentsPage = () => {
  const [outstandingInvoices, setOutstandingInvoices] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [invoicesRes, profileRes] = await Promise.all([
        invoicesAPI.getOutstanding(),
        designerAPI.getProfile(),
      ]);
      setOutstandingInvoices(invoicesRes.data);
      setProfile(profileRes.data);
    } catch (error) {
      toast.error('Failed to load pending payments');
    } finally {
      setLoading(false);
    }
  };

  const sendReminder = (invoice) => {
    const message = generatePaymentReminderMessage(invoice.customer_name, invoice);
    window.open(generateWhatsAppLink(invoice.customer_mobile, message), '_blank');
  };

  const totalOutstanding = outstandingInvoices.reduce((sum, inv) => sum + inv.balance, 0);

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><div className="text-[#6B7280]">Loading...</div></div>;
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-24 md:pb-8" data-testid="payments-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-['Chivo'] text-4xl md:text-5xl font-black tracking-tighter text-[#121212] mb-2">
            Pending Payments
          </h1>
          <p className="text-[#6B7280] font-medium leading-relaxed">Track outstanding invoices</p>
        </div>

        <Card className="border-[#E5E7EB] bg-white mb-6" data-testid="total-outstanding-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-red-50 p-3 rounded-full">
                  <Warning className="h-6 w-6 text-red-600" weight="duotone" />
                </div>
                <div>
                  <p className="text-sm text-[#6B7280]">Total Outstanding Amount</p>
                  <p className="font-['Chivo'] text-3xl font-black tracking-tighter text-red-600">
                    {formatCurrency(totalOutstanding)}
                  </p>
                </div>
              </div>
              <Badge className="bg-red-100 text-red-800">
                {outstandingInvoices.length} invoices
              </Badge>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          {outstandingInvoices.length === 0 ? (
            <Card className="border-[#E5E7EB] bg-white">
              <CardContent className="pt-6">
                <p className="text-center text-[#6B7280]">No pending payments. Great job!</p>
              </CardContent>
            </Card>
          ) : (
            outstandingInvoices.map((invoice) => (
              <Card key={invoice.id} data-testid={`pending-payment-${invoice.id}`} className="border-[#E5E7EB] bg-white hover:border-red-400 transition-colors">
                <CardContent className="pt-6">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-['Chivo'] text-lg font-bold text-[#121212]">{invoice.invoice_number}</h3>
                          <p className="text-sm text-[#6B7280]">{invoice.customer_name} • {invoice.customer_mobile}</p>
                          <p className="text-xs text-[#6B7280] mt-1">{formatDate(invoice.created_at)}</p>
                        </div>
                      </div>
                      <div className="bg-[#FAFAFA] p-4 rounded-lg">
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-[#6B7280]">Invoice Total</span>
                            <span className="font-medium text-[#121212]">{formatCurrency(invoice.total)}</span>
                          </div>
                          {invoice.advance_paid > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-[#6B7280]">Advance Paid</span>
                              <span className="text-green-600">{formatCurrency(invoice.advance_paid)}</span>
                            </div>
                          )}
                          <div className="border-t border-[#E5E7EB] pt-2">
                            <div className="flex justify-between">
                              <span className="font-bold text-[#121212]">Balance Due</span>
                              <span className="font-black text-2xl text-red-600">{formatCurrency(invoice.balance)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button
                        size="sm"
                        data-testid={`send-reminder-${invoice.id}`}
                        onClick={() => sendReminder(invoice)}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <WhatsappLogo className="h-4 w-4 mr-1" weight="fill" /> Send Reminder
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentsPage;